/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import * as Msdyn365 from '@msdyn365-commerce/core';
import { ControlledDrawer, getTelemetryObject, IModuleProps, INodeProps, ITelemetryContent, IDrawerState } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { observer } from 'mobx-react';
import * as React from 'react';

import { interactionStyle } from '../accordion/accordion.props.autogenerated';
import { IAccordionItemData } from './accordion-item.data';
import { IAccordionItemProps } from './accordion-item.props.autogenerated';

import { updateAccordionStates, updateAccordionState } from '../accordion/accordion';
import { AccordionStates } from '../../actions/get-accordion-state';

export interface IAccordionItemViewProps {
    AccordionItemContainer: IModuleProps;
    drawer: INodeProps;
    accordionSlots: INodeProps;
    AccordionItemContent: INodeProps;
    className: string;
}

export interface IAccordionItemExtentedProps extends IAccordionItemProps<IAccordionItemData> {
    interactionStyle?: interactionStyle;
}

/**
 *
 * AccordionItem component.
 * @extends {React.Component<IAccordionItemExtentedProps>}
 */
@observer
class AccordionItem extends React.Component<IAccordionItemExtentedProps> {
    private readonly moduleClassName: string = 'ms-accordion-item';

    private readonly telemetryContent?: ITelemetryContent;

    constructor(props: IAccordionItemProps<IAccordionItemData>) {
        super(props);
        this._onToggle = this._onToggle.bind(this);
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
    }

    public shouldComponentUpdate(nextProps: IAccordionItemProps<IAccordionItemData>): boolean {
        if (this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element {
        const { slots, renderView, config, data } = this.props;

        const accordionId = this.props.parentId || '0';
        const accordionItemId = this.props.id;
        const accordionStateResult = data.accordionState?.result|| {};
        const isAllExpanded = (accordionStateResult[accordionId] && accordionStateResult[accordionId].isAllExpanded === undefined)
            ? (data && config.isExpandedOnInitialLoad)
            : (accordionStateResult[accordionId] && accordionStateResult[accordionId].isAllExpanded);
        const isItemExpanded = accordionStateResult[accordionId] && accordionStateResult[accordionId].children && accordionStateResult[accordionId].children![accordionItemId];
        const isExpanded = isItemExpanded === undefined ? isAllExpanded : isItemExpanded;

        const viewProps = {
            ...this.props,
            AccordionItemContainer: {
                moduleProps: this.props,
                className: classnames(this.moduleClassName, config.className)
            },
            AccordionItemContent: {
                className: 'ms-accordion-item-content'
            },
            drawer: {
                tag: ControlledDrawer,
                className: `${this.moduleClassName}__drawer`,
                toggleButtonText: (config.accordionItemTitle && config.accordionItemTitle) || '',
                collapseProps: { isOpen: isExpanded },
                onToggle: (state: IDrawerState) => this._onToggle(state, this.props.context, accordionStateResult, accordionId, accordionItemId, !isExpanded),
                onTextChange: this.handleHeadingChange,
                requestContext: this.props.context.request,
                telemetryContent: this.telemetryContent
            },
            accordionSlots: slots
        };
        return renderView(viewProps) as React.ReactElement;
    }

    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent) => (this.props.config.accordionItemTitle = event.target.value);

    private _onToggle(state: IDrawerState, context: Msdyn365.ICoreContext, accordionStateResult: AccordionStates, accordionId: string, accordionItemId: string, expand: boolean): void {
        if (!state.isOpen && this.props.interactionStyle as string === interactionStyle.ExpandOneItemOnly) {
            updateAccordionStates(context, accordionStateResult, accordionId, false);
        } else {
            updateAccordionState(context, accordionStateResult, accordionId, accordionItemId, expand);
        }
    }
}
export default AccordionItem;
